import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { VERTICAL_MAP } from "@/lib/api";
import { useRegion } from "@/lib/region";
import RegionToggle from "@/components/RegionToggle";
import DiscoverLiberia from "@/components/DiscoverLiberia";
import { ArrowRight, Clock, Sparkle, Flame, ClockCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getRecent } from "@/lib/recentViews";
import RequestMatchModal from "@/components/RequestMatchModal";

const VERTICAL_IMAGES = {
  transport: "https://customer-assets-rejwkqb3.emergentagent.net/job_ridexelite-platform/artifacts/o7f53n2t_745411612_869933139521040_1687206371251555880_n.webp",
  home: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c",
  concierge: "https://images.pexels.com/photos/7820689/pexels-photo-7820689.jpeg",
  business: "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg",
};

export default function VerticalPage() {
  const { vertical } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popularKey, setPopularKey] = useState(null);
  const [recent, setRecent] = useState([]);
  const [matchPkg, setMatchPkg] = useState(null);

  const meta = VERTICAL_MAP[vertical];
  const { meta: regionMeta, region } = useRegion();

  useEffect(() => {
    setLoading(true);
    api.get(`/catalog/${vertical}`)
      .then(res => setData(res.data))
      .catch(() => toast.error("Could not load service"))
      .finally(() => setLoading(false));
  }, [vertical]);

  useEffect(() => {
    setPopularKey(null);
    api.get(`/catalog/${vertical}/popular`, { params: { region } })
      .then(res => setPopularKey(res.data?.lookup_key || null))
      .catch(() => {});
  }, [vertical, region]);

  // Load recently-viewed items scoped to the current region, excluding whatever is already listed on this same vertical page.
  useEffect(() => {
    const all = getRecent().filter(r => r.region === region && r.vertical !== vertical);
    setRecent(all.slice(0, 3));
  }, [vertical, region]);

  if (!meta) return <div className="max-w-4xl mx-auto p-10 text-white">Unknown vertical.</div>;
  const accent = meta.color;

  const handleBook = (pkg) => {
    navigate(`/book/${vertical}/${pkg.lookup_key}`);
  };

  return (
    <div data-testid={`vertical-page-${vertical}`}>
      {matchPkg && <RequestMatchModal pkg={matchPkg} region={region} onClose={() => setMatchPkg(null)} />}
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <img src={VERTICAL_IMAGES[vertical]} alt={meta.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/70 to-[#09090B]/30" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}30, transparent 60%)` }} />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16">
          <div className="eyebrow gold-text mb-4">RideXelite · Vertical 0{["transport","home","concierge","business"].indexOf(vertical)+1}</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none">
            {data?.name?.replace("RideXelite ", "") || meta.name}
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-2xl">{data?.tagline || meta.tag}</p>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-4">Services & Pricing</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-white">Choose your package</h2>
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-zinc-400">
              <span>{regionMeta.flag}</span>
              Pricing shown for <span className="text-white">{regionMeta.label}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <RegionToggle />
            <Link to="/" className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">← Back to overview</Link>
          </div>
        </div>

        {loading && <div className="text-zinc-400">Loading…</div>}

        {recent.length > 0 && !loading && (
          <div className="mb-14" data-testid="recently-viewed-strip">
            <div className="flex items-center gap-3 mb-4">
              <ClockCounterClockwise weight="duotone" size={16} className="text-[#D4AF37]" />
              <div className="eyebrow gold-text">Recently viewed</div>
              <div className="text-xs text-zinc-500 tracking-[0.2em] uppercase">Pick up where you left off</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {recent.map(r => (
                <Link
                  key={`${r.vertical}-${r.lookup_key}`}
                  to={`/book/${r.vertical}/${r.lookup_key}`}
                  data-testid={`recently-viewed-${r.lookup_key}`}
                  className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121214] hover:bg-black/40 hover:border-[#D4AF37]/50 transition-colors px-4 py-3"
                >
                  <div
                    className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center"
                    style={{
                      background: `${(VERTICAL_MAP[r.vertical]?.color) || "#1E3A8A"}30`,
                      border: `1px solid ${(VERTICAL_MAP[r.vertical]?.color) || "#1E3A8A"}`,
                    }}
                  >
                    <Clock weight="duotone" size={16} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm font-medium truncate group-hover:text-[#D4AF37] transition-colors">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">{r.vertical}</div>
                  </div>
                  <ArrowRight size={14} className="text-zinc-500 group-hover:text-[#D4AF37] transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {!loading && data && (() => {
          const visible = data.packages.filter(pkg => !pkg.region_overrides?.[region]?.hidden);
          const bundles = visible.filter(pkg => pkg.lookup_key.includes("bundle"));
          const regular = visible.filter(pkg => !pkg.lookup_key.includes("bundle"));
          const renderCard = (pkg, keyPrefix = "") => {
            const override = pkg.region_overrides?.[region];
            const localPrice = override?.amount != null
              ? override.amount / 100
              : (pkg.amount * regionMeta.priceMultiplier) / 100;
            const desc = override?.description || pkg.description;
            const perks = override?.perks || [];
            const isBundle = pkg.lookup_key.includes("bundle");
            const isPopular = popularKey && pkg.lookup_key === popularKey;
            // Under Liberia, "Find a good X" services switch to a lead-capture flow instead of direct booking.
            const isRequestMatch = region === "liberia" && pkg.lookup_key.includes("_find_");
            return (
              <div
                key={keyPrefix + pkg.lookup_key}
                data-testid={`package-${pkg.lookup_key}`}
                className={`group relative rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
                  isBundle
                    ? "border-[#D4AF37] bg-gradient-to-br from-[#1a1610] via-[#121214] to-[#1a1815] shadow-[0_18px_60px_-20px_rgba(212,175,55,0.35)]"
                    : override
                    ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#121214] to-[#1a1815]"
                    : "border-white/10 bg-[#121214] hover:border-[#D4AF37]/50"
                }`}
              >
                {isBundle && (
                  <div
                    data-testid={`bundle-badge-${pkg.lookup_key}`}
                    className="absolute -top-3 left-6 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.22em] font-semibold shadow-[0_10px_25px_-8px_rgba(212,175,55,0.6)]"
                  >
                    <Sparkle weight="fill" size={11} /> Best Value · Bundle
                  </div>
                )}
                {isPopular && (
                  <div
                    data-testid={`popular-badge-${pkg.lookup_key}`}
                    className="absolute -top-3 right-6 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#F97316] to-[#DC2626] text-white text-[10px] uppercase tracking-[0.22em] font-semibold shadow-[0_10px_25px_-8px_rgba(249,115,22,0.55)]"
                  >
                    <Flame weight="fill" size={11} /> Popular in {regionMeta.label}
                  </div>
                )}
                {isRequestMatch && !isPopular && (
                  <div
                    data-testid={`match-badge-${pkg.lookup_key}`}
                    className="absolute -top-3 right-6 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] uppercase tracking-[0.22em] font-semibold"
                  >
                    <MagnifyingGlass weight="bold" size={11} /> Concierge match
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="w-1 h-10 rounded-full" style={{ background: accent }} />
                  {override && (
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                      {regionMeta.flag} Flat rate
                    </span>
                  )}
                </div>
                <div className="font-serif-display text-2xl text-white mb-2 mt-6">{pkg.name}</div>
                <div className="text-sm text-zinc-400 leading-relaxed min-h-[3rem]">{desc}</div>

                {perks.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {perks.map(p => (
                      <li key={p} className="text-xs text-zinc-300 flex items-start gap-2">
                        <span className="text-[#D4AF37] mt-[3px]">◆</span> {p}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-8 flex items-baseline gap-1">
                  <span className="font-serif-display text-5xl text-white">${localPrice.toFixed(0)}</span>
                  <span className="text-zinc-500 text-sm">USD {override ? "· flat" : ""}</span>
                </div>
                <div className="mt-2 text-xs text-zinc-500">{regionMeta.flag} {regionMeta.label} pricing</div>
                <div className="mt-4 flex items-center gap-2 text-zinc-500 text-sm">
                  <Clock size={14} /> {pkg.duration_min} min
                </div>
                <button
                  onClick={() => isRequestMatch ? setMatchPkg({ ...pkg, vertical }) : handleBook(pkg)}
                  data-testid={`book-btn-${pkg.lookup_key}`}
                  className={`mt-8 w-full py-3 rounded-full text-white text-sm font-semibold border transition-all flex items-center justify-center gap-2 ${
                    isRequestMatch
                      ? "bg-transparent border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37]"
                      : "bg-white/5 hover:bg-[#D4AF37] hover:text-black border-white/10 hover:border-[#D4AF37]"
                  }`}
                >
                  {isRequestMatch ? (
                    <>Request a match <MagnifyingGlass size={14} weight="bold" /></>
                  ) : (
                    <>Book now <ArrowRight size={14} weight="bold" /></>
                  )}
                </button>
              </div>
            );
          };

          return (
            <>
              {bundles.length > 0 && (
                <div className="mb-16" data-testid="featured-bundles-strip">
                  <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Sparkle weight="fill" size={14} className="text-[#D4AF37]" />
                      <div className="eyebrow gold-text">Featured Bundles · Best Value</div>
                    </div>
                    <div className="text-xs text-zinc-500 tracking-[0.2em] uppercase">Save vs booking individually</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {bundles.map(pkg => renderCard(pkg, "feat-"))}
                  </div>
                  <div className="h-px bg-white/10 mt-16" />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="packages-grid">
                {regular.map(pkg => renderCard(pkg))}
              </div>
            </>
          );
        })()}
      </section>

      {/* Concierge · Liberia · Discover Directory */}
      {vertical === "concierge" && region === "liberia" && <DiscoverLiberia />}
    </div>
  );
}
